import type { useTranslate } from "ra-core";

import { APP_LOCALE, NUMERIC_DATE_FORMAT } from "../misc/locale";
import type { IncentiveProgram, QuarterlyOutstandingSalesTier } from "../types";
import { formatMxn } from "./IncentiveBars";

/**
 * Almikia Epic E7 (TASK-095): plain functions backing the "Incentivos"
 * admin screen -- validators, choice lists and the plain-Spanish parameter
 * summary shown in the list -- kept free of react-admin/form coupling so
 * they are unit-testable on their own (see `incentiveProgramForm.test.ts`).
 */

type Translate = ReturnType<typeof useTranslate>;

export const INCENTIVE_PROGRAM_PERIOD_CHOICES = [
  { id: "month", name: "resources.incentive_programs.period.month" },
  { id: "quarter", name: "resources.incentive_programs.period.quarter" },
  {
    id: "accumulated",
    name: "resources.incentive_programs.period.accumulated",
  },
];

export const INCENTIVE_PROGRAM_SCOPE_CHOICES = [
  { id: "advisor", name: "resources.incentive_programs.scope.advisor" },
  { id: "team", name: "resources.incentive_programs.scope.team" },
];

// `timeZone: "UTC"` is required here, not decorative: this formats a
// `Date.UTC(...)`-built instant, and without it the formatter falls back to
// the HOST's local timezone -- on a host behind UTC (e.g. America/Mexico_City,
// this app's own timezone) that rolls a date-only value back a full day,
// breaking this function's own "never shifted by the host timezone" promise.
const DATE_ONLY_FORMAT = new Intl.DateTimeFormat(APP_LOCALE, {
  ...NUMERIC_DATE_FORMAT,
  timeZone: "UTC",
});

/** Formats a `YYYY-MM-DD` date-only string, never shifted by the host timezone. */
export function formatDateOnly(dateOnly: string): string {
  const [year, month, day] = dateOnly.split("-").map(Number);
  return DATE_ONLY_FORMAT.format(new Date(Date.UTC(year, month - 1, day)));
}

/** `dateOnly` shifted by `days` (may be negative), still a `YYYY-MM-DD` string. */
export function dateOnlyPlusDays(dateOnly: string, days: number): string {
  const [year, month, day] = dateOnly.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** The validity column: "Desde %{from}" when open-ended, "%{from} - %{to}" otherwise. */
export function formatValidityRange(
  program: Pick<IncentiveProgram, "valid_from" | "valid_to">,
  translate: Translate,
): string {
  const from = formatDateOnly(program.valid_from);
  if (!program.valid_to) {
    return translate("resources.incentive_programs.validity.open_ended", {
      from,
    });
  }
  return translate("resources.incentive_programs.validity.range", {
    from,
    to: formatDateOnly(program.valid_to),
  });
}

/**
 * The list's "key parameters in plain Spanish" column (e.g.
 * "0.05 % por venta, tope $1,250"), read from `program.parameters` DATA --
 * never a hardcoded value. `best_bpo_team`/`gasoline_insurance` define no
 * parameters yet (DATA CONTRACT E7).
 */
export function describeIncentiveProgramParameters(
  program: IncentiveProgram,
  translate: Translate,
): string {
  switch (program.code) {
    case "monthly_sale_incentive":
      return translate(
        "resources.incentive_programs.summary.monthly_sale_incentive",
        {
          rate: program.parameters.rate_pct,
          cap: formatMxn(program.parameters.cap_mxn_per_sale),
        },
      );
    case "quarterly_outstanding_sales":
      return translate(
        "resources.incentive_programs.summary.quarterly_outstanding_sales",
        {
          min_units: program.parameters.min_units,
          tiers_count: program.parameters.tiers.length,
        },
      );
    case "monthly_proactive_appointments":
      return translate(
        "resources.incentive_programs.summary.monthly_proactive_appointments",
        {
          target: program.parameters.target_appointments,
          reward: formatMxn(program.parameters.reward_max_mxn),
        },
      );
    case "best_bpo_team":
    case "gasoline_insurance":
      return translate("resources.incentive_programs.summary.no_parameters");
  }
}

/**
 * react-hook-form validator: any number field here (rate, cap, min units,
 * target appointments, max reward, tier thresholds/rates) must be 0 or more
 * -- the acceptance criterion's "negative numbers are rejected" rule.
 */
export function validateNonNegativeNumber(
  value: number | string | null | undefined,
) {
  if (value == null || value === "") return undefined;
  const num = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(num) || num < 0) {
    return "crm.incentives.error.negative_number";
  }
  return undefined;
}

/**
 * Cross-field validator for `valid_to`: when set, it must not fall before
 * `valid_from` (both are date-only strings, so plain string comparison is
 * exact).
 */
export function validateValidTo(
  value: string | null | undefined,
  allValues: { valid_from?: string },
) {
  if (!value) return undefined;
  if (allValues.valid_from && value < allValues.valid_from) {
    return "crm.incentives.error.valid_to_before_valid_from";
  }
  return undefined;
}

/**
 * `<ArrayInput source="parameters.tiers">`'s validator: every tier's
 * threshold/rate must be 0 or more, and thresholds must be STRICTLY
 * ascending (DATA CONTRACT E7: "tiers: ascending by threshold_mxn").
 */
export function validateTiers(
  value: QuarterlyOutstandingSalesTier[] | null | undefined,
) {
  if (!value || value.length === 0) return undefined;
  for (const tier of value) {
    if (
      tier == null ||
      tier.threshold_mxn == null ||
      tier.threshold_mxn < 0 ||
      tier.rate_pct == null ||
      tier.rate_pct < 0
    ) {
      return "crm.incentives.error.negative_number";
    }
    // DATA CONTRACT E7's tier shape requires `inclusive` -- without this
    // check a tier added through the form saves without that key and is
    // silently treated as exclusive by `incentiveProgress.ts`.
    if (typeof tier.inclusive !== "boolean") {
      return "crm.incentives.error.tier_inclusive_required";
    }
  }
  for (let index = 1; index < value.length; index += 1) {
    if (value[index].threshold_mxn <= value[index - 1].threshold_mxn) {
      return "crm.incentives.error.tiers_not_ascending";
    }
  }
  return undefined;
}

/**
 * "Nueva vigencia" (TASK-095): the new version's `valid_from` must be
 * strictly later than the CURRENT program's own end -- its `valid_to` when
 * already set, otherwise its `valid_from` -- so the two-step
 * update-then-create in `NewVigenciaDialog` never asks the database to
 * accept an overlapping window in the first place.
 */
export function validateNewValidFrom(
  current: Pick<IncentiveProgram, "valid_from" | "valid_to">,
  value: string | null | undefined,
): string | undefined {
  if (!value) return "ra.validation.required";
  const floor = current.valid_to ?? current.valid_from;
  if (value <= floor) {
    return "crm.incentives.error.new_valid_from_too_early";
  }
  return undefined;
}
